import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { bind } from 'bind-decorator';

import { checkLoginStatus } from 'auth/actions';
import { authLocalStorageManager } from 'auth/utils';
import { Icon, Modal } from 'elements';
import { ClassroomsApi, UsersApi } from 'global/api';
import { ROUTES } from 'global/constants';
import { getStore } from 'global/store';
import { isKeyboardEvent } from 'global/utils';
import { LocationDescriptor } from 'history';
import { Profile, ProfileOnboardingStatusEnum, ProfileRoleEnum } from 'labxchange-client';
import { showErrorMessage } from 'ui/components/GlobalMessageReporter/dispatch';
import { ImagePickerData } from 'ui/components/ImageFilePicker/ImageFilePicker';
import { UserAvatar } from 'user/components';
import { WrappedMessage } from 'utils';
import messages from '../displayMessages';
import { EducatorSignupDetails, LearnerAtSchoolSignupDetails, LearnerSignupDetails } from './OnboardingDetailsForms';
import { intl } from 'i18n';

enum ModalType {
    None = 0,
    ClassCodeError,
}

interface State {
    classCode?: string;
    redirectTo?: LocationDescriptor;
    modalType: ModalType;
}

interface Props {
    profile: Profile;
    // An event we emit when the user's avatar is updated, to tell the parent component to refresh the profile data.
    onAvatarUpdate: () => void;
}

export class OnboardingDetails extends React.PureComponent<Props, State> {
    private age?: number;

    constructor(props: Props) {
        super(props);
        this.state = {
            modalType: ModalType.None,
        };

        const today = new Date();
        if (this.props.profile.dateOfBirth !== undefined) {
            this.age = today.getFullYear() - this.props.profile.dateOfBirth.getFullYear();
        }
    }

    public render() {
        if (this.state.redirectTo) { return <Redirect to={this.state.redirectTo} push={true}/>; }

        const user = this.props.profile;

        if (this.age === undefined) {
            return <Redirect to={ROUTES.General.SIGN_UP} push={true}/>;
        }

        return (
            <div className='signup-details'>
                <h1><WrappedMessage message={messages.profileDetailsTitle}/></h1>
                <div className='signup-details-box'>
                    <button className='profile-image unstyled text-header'
                            onKeyPress={() => {
                            document.getElementById('user-profile-image-picker')!.click();
                    }}>
                        <UserAvatar
                            avatarStyle='large-profile'
                            username={user.username}
                            diameter='120px'
                            displayText={intl.formatMessage(this.props.profile.publicAvatarUrl ?
                                messages.changeImageText : messages.uploadImageText)}
                            onFileChanged={this.updateProfileImage}
                        />
                    </button>
                    <div className='remove-image'>
                        {this.props.profile.publicAvatarUrl
                        ?   <>
                                <Icon name='x' zoom='15' />
                                <input type='button' value='Remove' onClick={this.onRemoveImage}
                                    tabIndex={0} onKeyPress={this.onRemoveImage}/>
                                <span className='sr-only'>
                                    <WrappedMessage message={messages.removeImageText} />
                                </span>
                            </>
                        : null}
                    </div>
                    <h2>{user.fullName}</h2>
                    <div className='username'>@{user.username}</div>
                    <form onSubmit={(e) => { e.preventDefault(); }}>
                        {this.getRoleDetails(user.role, this.age)}
                        <div className='signup-details-buttons'>
                            <button className='btn btn-outline-primary' type='button' onClick={() => this.onSkip()}>
                                <WrappedMessage message={messages.skipRoleRegistrationButton}/>
                            </button>
                            <button className='btn yellow' type='submit' onClick={this.onFinish}>
                                <WrappedMessage message={messages.finishButtonLabel}/>
                            </button>
                        </div>
                    </form>
                </div>
                {this.showModal()}
            </div>
        );
    }

    private showModal() {
        switch (this.state.modalType) {
            case ModalType.ClassCodeError: {
                return (
                    <Modal
                        onRequestClose={() => this.setState({modalType: ModalType.None})}
                        className='class-code-error-modal'
                        content={
                            <>
                                <div className='icon-warning'></div>
                                <h1 className='title'>
                                    <WrappedMessage message={messages.invalidClassCodeModalTitle}/>
                                </h1>
                                <div className='description'>
                                    <WrappedMessage message={messages.invalidClassCodeModalDescription}/>
                                </div>
                                <div className='buttons'>
                                    <button className='btn primary-btn btn-outline-primary'
                                        onClick={() => this.setState({modalType: ModalType.None})}>
                                        <WrappedMessage message={messages.cancelButtonLabel}/>
                                    </button>
                                    <button className='btn primary-btn'
                                        onClick={() => this.setState({modalType: ModalType.None})}>
                                        <WrappedMessage message={messages.tryAgainButtonLabel}/>
                                    </button>
                                </div>
                            </>
                        }
                    />

                );
            }
            case ModalType.None: {
                break;
            }
        }
        return;
    }

    private getRoleDetails(role: ProfileRoleEnum, age: number) {
        switch (role) {
            case ProfileRoleEnum.Learner: {
                if (age < 18) {
                    return (
                        <LearnerAtSchoolSignupDetails
                            profile={this.props.profile}
                            onUpdateClassCode={(classCode) => { this.setState({classCode}); }}
                        />
                    );
                } else {
                    return (
                        <LearnerSignupDetails
                            profile={this.props.profile}
                            onUpdateClassCode={(classCode) => { this.setState({classCode}); }}
                        />
                    );
                }
            }
            case ProfileRoleEnum.Educator:
                return (
                    <EducatorSignupDetails
                        profile={this.props.profile}
                    />
                );

        }
    }

    private async onSkip() {
        const completed = ProfileOnboardingStatusEnum.Completed;
        const profile = this.props.profile;
        const data = {onboarding_status: completed};
        await UsersApi.profilesPartialUpdate({id: profile.username, data});
        authLocalStorageManager.onboardingStatus = completed;
        checkLoginStatus(getStore().dispatch);
        this.setState({redirectTo: ROUTES.Explore.HOME});
    }

    @bind private async onFinish() {
        const profile = this.props.profile;
        const joinCode = this.state.classCode;

        if (joinCode !== undefined && joinCode) {
            try {
                await ClassroomsApi.join({data: {joinCode}});
            } catch (response) {
                if ([400, 404].includes(response.status)) {
                    this.setState({modalType: ModalType.ClassCodeError});
                    return;
                }
            }
        }

        const completed = ProfileOnboardingStatusEnum.Completed;
        try {
            await UsersApi.profilesPartialUpdate({
                id: profile.username,
                data: {onboarding_status: completed}});
            authLocalStorageManager.onboardingStatus = completed;
            checkLoginStatus(getStore().dispatch);
            this.setState({redirectTo: ROUTES.Explore.HOME});
        } catch (err) {
            showErrorMessage(
                <WrappedMessage message={messages.errorMessageUnableToUpdateUserProfile}/>,
                {
                    exception: err,
                    details: 'Unable to update user profile',
                },
            );
            return;
        }
    }

    @bind private async updateProfileImage(data: ImagePickerData) {
        // TODO: refactor this and the duplicate function in
        // user/components/UserProfile/UserProfile.tsx into a single function
        const username = this.props.profile.username;
        const image = data.image;
        if (image === null) { return; }

        try {
            await UsersApi.profilesImageCreate({id: username, image});
        } catch (exception) {
            showErrorMessage(
                <WrappedMessage message={messages.errorMessageUnableToUpdateUserProfile}/>,
                {exception},
            );
            return;
        }
        this.props.onAvatarUpdate();
    }

    @bind private onRemoveImage(event: React.MouseEvent<HTMLInputElement>|React.KeyboardEvent<HTMLInputElement>) {
        if (isKeyboardEvent(event)) {
            if (event.key !== 'Enter') { return; }
        }
        this.removeProfileImage();
    }

    private async removeProfileImage() {
        const username = this.props.profile.username;
        try {
            await UsersApi.profilesImageDelete({id: username});
        } catch (err) {
            showErrorMessage(
                <WrappedMessage message={messages.errorMessageUnableToUpdateUserProfile}/>,
                {
                    exception: err,
                    details: 'Unable to update user profile',
                },
            );
        }
        this.props.onAvatarUpdate();
    }
}
